import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import axiosClient from "../../axiosClient";
import { Loader } from "rsuite";
import { Question } from "../../types";
import ReadOnlyQuestionsView from "../Shared/ReadOnlyQuestionsView";
import { useUser } from "../../context/UserContext";
import { getSubjectTrial } from "../../lib/trial";

const BATCH_SIZE = 10;

type CompletedPracticeResponse = {
  questions: Question[];
  totalCount: number;
  skip: number;
  limit: number;
};

const ReviewPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const prefetchedForLengthRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { trial, accessStatus } = useUser();
  const isTrialSubject = getSubjectTrial(trial, accessStatus, searchParams.get("subjectId")) !== null;

  const buildQueryParams = useCallback(
    (skip: number) => {
      const subjectId = searchParams.get("subjectId");
      const moduleIds = searchParams.get("moduleIds");
      const params = new URLSearchParams();

      if (subjectId) {
        params.set("subjectId", subjectId);
      } else if (moduleIds) {
        params.set("moduleIds", moduleIds);
      }

      params.set("skip", String(skip));
      params.set("limit", String(BATCH_SIZE));
      return `?${params.toString()}`;
    },
    [searchParams],
  );

  const fetchCompletedQuestions = useCallback(
    async (skip: number, append: boolean) => {
      const response = await axiosClient.get<CompletedPracticeResponse>(
        `/practice-questions-completed${buildQueryParams(skip)}`,
      );
      const { questions: batch, totalCount: total } = response.data;

      setTotalCount(total);
      setQuestions((prev) => (append ? prev.concat(batch) : batch));

      if (!append && batch.length === 0) {
        return;
      }
    },
    [buildQueryParams],
  );

  useEffect(() => {
    const loadInitial = async () => {
      try {
        await fetchCompletedQuestions(0, false);
        setIsLoading(false);
      } catch (error: any) {
        if (error?.response?.status !== 429) {
          navigate("/dashboard");
        } else {
          setIsLoading(false);
        }
      }
    };

    loadInitial();
  }, [fetchCompletedQuestions, navigate]);

  const loadMore = useCallback(async (): Promise<number> => {
    if (isFetchingMore) return 0;
    if (totalCount > 0 && questions.length >= totalCount) return 0;

    setIsFetchingMore(true);
    try {
      const response = await axiosClient.get<CompletedPracticeResponse>(
        `/practice-questions-completed${buildQueryParams(questions.length)}`,
      );
      const { questions: batch, totalCount: total } = response.data;

      setTotalCount(total);
      setQuestions((prev) => prev.concat(batch));

      if (batch.length > 0) {
        prefetchedForLengthRef.current = null;
      }

      return batch.length;
    } catch (error: any) {
      if (error?.response?.status !== 429) {
        navigate("/dashboard");
      }
      return 0;
    } finally {
      setIsFetchingMore(false);
    }
  }, [buildQueryParams, isFetchingMore, navigate, questions.length, totalCount]);

  const handleApproachingEnd = useCallback(async (): Promise<number> => {
    if (prefetchedForLengthRef.current === questions.length) {
      return 0;
    }
    if (totalCount > 0 && questions.length >= totalCount) {
      return 0;
    }

    prefetchedForLengthRef.current = questions.length;
    return loadMore();
  }, [loadMore, questions.length, totalCount]);

  if (isLoading) {
    return <Loader size="md" content="Loading..." style={{ margin: "auto" }} />;
  }

  return (
    <ReadOnlyQuestionsView
      questions={questions}
      totalQuestionCount={totalCount}
      pageId="ReviewPage"
      onQuestionsUpdate={setQuestions}
      onApproachingEnd={handleApproachingEnd}
      isLoadingMore={isFetchingMore}
      allowStarring={!isTrialSubject}
    />
  );
};

export default ReviewPage;
